import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import PermissionFilterParameters from "permissions/_api/queries/filtering/permission-filter-parameters";
import PermissionOrderParameters from "permissions/_api/queries/ordering/permission-order-params";
import { canonicalQueryCacheKey } from "permissions/_api/queries/canonical-query-cache-key";

export enum PermissionView {
  Flat,
  Grantee,
}

export type PermissionQueryParams = {
  filterParameters?: PermissionFilterParameters;
  orderParameters?: PermissionOrderParameters;
  paginationParameters?: PaginationParameters;
  permissionView?: PermissionView;
  previewSize?: number;
  includePermissionPreviews?: boolean;
};

export default class PermissionQuery {
  public readonly filterParameters?: PermissionFilterParameters;
  public readonly orderParameters?: PermissionOrderParameters;
  public readonly paginationParameters?: PaginationParameters;
  public readonly permissionView?: PermissionView;
  public readonly previewSize?: number;
  public readonly includePermissionPreviews?: boolean;

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...(this.filterParameters?.searchParams ?? []),
      ...(this.orderParameters?.searchParams ?? []),
      ...(this.paginationParameters?.searchParams ?? []),
      ...(this.permissionView === undefined ? [] : [["view", PermissionView[this.permissionView]]]),
      ...(this.previewSize === undefined ? [] : [["previewSize", this.previewSize.toString()]]),
      ...(this.includePermissionPreviews === undefined
        ? []
        : [["includePermissionPreviews", this.includePermissionPreviews.toString()]]),
    ]);
  }

  public get cacheKey(): string {
    return canonicalQueryCacheKey(this.searchParams);
  }

  public constructor(params: PermissionQueryParams) {
    this.filterParameters = params.filterParameters;
    this.orderParameters = params.orderParameters;
    this.paginationParameters = params.paginationParameters;
    this.permissionView = params.permissionView;
    this.previewSize = params.previewSize;
    this.includePermissionPreviews = params.includePermissionPreviews;
  }

  public isEqualTo(other: PermissionQuery | undefined): boolean {
    if (!other) return false;

    const filtersEqual =
      (!this.filterParameters && !other.filterParameters) ||
      (this.filterParameters && other.filterParameters && this.filterParameters.isEqualTo(other.filterParameters));

    const orderEqual =
      (!this.orderParameters && !other.orderParameters) ||
      (this.orderParameters && other.orderParameters && this.orderParameters.isEqualTo(other.orderParameters));

    const paginationEqual =
      (!this.paginationParameters && !other.paginationParameters) ||
      (this.paginationParameters &&
        other.paginationParameters &&
        this.paginationParameters.isEqualTo(other.paginationParameters));

    return Boolean(
      filtersEqual &&
        orderEqual &&
        paginationEqual &&
        this.permissionView === other.permissionView &&
        this.previewSize === other.previewSize &&
        this.includePermissionPreviews === other.includePermissionPreviews,
    );
  }

  public toString(): string {
    return (
      `PermissionQuery {\n` +
      `Filter Parameters: { ${this.filterParameters?.toString() ?? "None"} },\n` +
      `Order Parameters: { ${this.orderParameters?.toString() ?? "None"} },\n` +
      `Pagination Parameters: { ${this.paginationParameters?.toString() ?? "None"} },\n` +
      `Permission View: ${this.permissionView === undefined ? "None" : PermissionView[this.permissionView]},\n` +
      `Preview Size: ${this.previewSize ?? "None"},\n` +
      `Include Permission Previews: ${this.includePermissionPreviews ?? "None"}\n` +
      `}`
    );
  }
}
